import React, { useCallback, useEffect, useState } from 'react';
import { fetchAiConnections, saveAiConnection, deleteAiConnection } from '../services/api';
import { Button, Card, Field, Loading, ErrorText, errorMessage } from '../components/ui';
import { PROVIDERS, modelsForProvider, providerLabel, endpointHint, endpointRequired, isKnownModel, supportsBaseUrl } from '@shared/aiModels';
import { useMe } from '../hooks/useMe';

const BLANK = {
  label: '', providerType: 'openai', model: '', apiKey: '',
  baseUrl: '', endpoint: '', deployment: '', apiVersion: '', isDefault: false,
};

// Bring-your-own AI credentials. The key is written straight to the backend,
// which encrypts it at rest - it is never stored in the browser.
//
// The model is a datalist-backed combo rather than a plain text box: a typo in
// a model name is not caught here, it surfaces much later as an opaque provider
// error on the first agent run. Typing is still allowed, because Azure
// deployments carry whatever name the tenant gave them and providers ship new
// models faster than the catalogue is updated.
export default function AiConnectionsPage() {
  // Adding or deleting a connection is admin-only server-side. Members can see
  // which models exist - useful context for the cost figures - but not change
  // them, and never the keys, which are never sent to any client.
  const { isAdmin } = useMe();
  const [connections, setConnections] = useState([]);
  const [draft, setDraft] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try { setConnections(await fetchAiConnections()); } catch (err) { setError(errorMessage(err)); }
  }, []);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  // Switching provider clears the model: a model id is only meaningful for the
  // provider it belongs to, and carrying "gpt-4o" over to Anthropic would post
  // a name that provider will reject.
  function chooseProvider(providerType) {
    set({ providerType, model: '' });
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await saveAiConnection({
        ...draft,
        label: draft.label.trim(),
        model: draft.model.trim(),
        apiKey: draft.apiKey.trim(),
      });
      setDraft(BLANK);
      load();
    } catch (err) { setError(errorMessage(err)); } finally { setSaving(false); }
  }

  const models = modelsForProvider(draft.providerType);
  const isAzure = draft.providerType === 'azure_openai';
  const unknownModel = draft.model.trim() && !isKnownModel(draft.providerType, draft.model);

  return (
    <>
      <h1>AI connections</h1>
      <p className="sub">Models available to your agents. Keys are stored encrypted by the backend, never in this browser.</p>
      <ErrorText>{error}</ErrorText>

      {loading ? <Loading /> : connections.map((c) => (
        <Card
          key={c.id}
          title={c.label}
          subtitle={[providerLabel(c.providerType), c.model, c.isDefault ? 'Default' : ''].filter(Boolean).join(' · ')}
        >
          {isAdmin && <Button compact danger onClick={async () => { await deleteAiConnection(c.id); load(); }}>Delete</Button>}
        </Card>
      ))}

      {!isAdmin && !loading && (
        <p className="muted">Only an owner or admin can add or change AI connections.</p>
      )}

      {isAdmin && (
      <Card title="Add a connection">
        <Field label="Name">
          <input className="input" value={draft.label} onChange={(e) => set({ label: e.target.value })} placeholder="e.g. Team Gemini key" />
        </Field>

        <Field label="Provider">
          <select className="input" value={draft.providerType} onChange={(e) => chooseProvider(e.target.value)}>
            {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </Field>

        <Field
          label="Model"
          hint={unknownModel
            ? 'Not a model we know about — it will still be saved, but its cost will be estimated at a fallback rate.'
            : (isAzure ? 'Pick one, or type your deployment name.' : 'Pick one, or type any model your provider supports.')}
        >
          <input
            className="input"
            list="ai-model-options"
            value={draft.model}
            onChange={(e) => set({ model: e.target.value })}
            placeholder={models[0] ? models[0].id : 'e.g. gpt-4.1-mini'}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
          />
          <datalist id="ai-model-options">
            {models.map((m) => (
              <option key={m.id} value={m.id}>{[m.label, m.hint].filter(Boolean).join(' — ')}</option>
            ))}
          </datalist>
        </Field>

        {isAzure ? (
          <>
            <Field label="Endpoint" hint={endpointHint(draft.providerType)}>
              <input className="input" value={draft.endpoint} onChange={(e) => set({ endpoint: e.target.value })} placeholder="https://<resource>.openai.azure.com" />
            </Field>
            <Field label="Deployment" hint="The deployment name in your Azure resource, if it differs from the model.">
              <input className="input" value={draft.deployment} onChange={(e) => set({ deployment: e.target.value })} />
            </Field>
            <Field label="API version">
              <input className="input" value={draft.apiVersion} onChange={(e) => set({ apiVersion: e.target.value })} placeholder="2024-08-01-preview" />
            </Field>
          </>
        ) : supportsBaseUrl(draft.providerType) ? (
          <Field label="Base URL" hint={endpointHint(draft.providerType)}>
            <input className="input" value={draft.baseUrl} onChange={(e) => set({ baseUrl: e.target.value })} placeholder="Leave blank for the default" />
          </Field>
        ) : null}

        <Field label="API key">
          <input className="input" type="password" value={draft.apiKey} onChange={(e) => set({ apiKey: e.target.value })} autoComplete="new-password" />
        </Field>

        <Field>
          <label className="check-row">
            <input type="checkbox" checked={draft.isDefault} onChange={(e) => set({ isDefault: e.target.checked })} />
            <span>Use as the default connection</span>
          </label>
        </Field>

        <Button
          onClick={handleSave}
          disabled={!draft.label.trim() || !draft.model.trim() || !draft.apiKey.trim() || (endpointRequired(draft.providerType) && !draft.endpoint.trim()) || saving}
        >
          {saving ? 'Saving…' : 'Save connection'}
        </Button>
      </Card>
      )}
    </>
  );
}
